import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Paper, TextField, Button, Grid, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import validator from 'validator';

const PatientView = () => {
    const [patients, setPatients] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [newPatient, setNewPatient] = useState({
        nombre: '',
        birthdate: '',
        sexo: '',
        edocivil: '',
        numid: '',
        domicilio: '',
        email: '',
        asegurador: '',
        tel: '',
        notas: '',
        proxcita: '',
        foto: '',
        aviso: ''
    });
    const [age, setAge] = useState('');
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletePatientId, setDeletePatientId] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL;

    const fetchPatients = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/identificacion`);
            setPatients(response.data.map((patient, index) => ({
                id: patient.idpac, // Use the actual id from the database
                nombre: patient.nombre,
                birthdate: patient.birthdate,
                sexo: patient.sexo,
                edocivil: patient.edocivil,
                numid: patient.numid,
                domicilio: patient.domicilio,
                email: patient.email,
                asegurador: patient.asegurador,
                tel: patient.tel,
                notas: patient.notas,
                proxcita: patient.proxcita,
                foto: patient.foto,
                aviso: patient.aviso
            })));
        } catch (error) {
            console.error('Error al obtener los datos de los pacientes:', error);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchPatients();
    }, [fetchPatients]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPatient({ ...newPatient, [name]: value });
        if (name === 'birthdate') {
            const birthDate = new Date(value);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                setAge(age - 1);
            } else {
                setAge(age);
            }
            if (age < 1) {
                const months = m < 0 ? (12 + m) : m;
                setAge(`${months} meses`);
            }
        }
    };

    const handleAddPatient = async () => {
        try {
            const response = await axios.post(`${API_URL}/api/identificacion`, newPatient);
            setPatients([...patients, { ...response.data, id: response.data.id }]);
        } catch (error) {
            console.error('Error al agregar el paciente:', error);
        }
        setNewPatient({
            nombre: '',
            birthdate: '',
            sexo: '',
            edocivil: '',
            numid: '',
            domicilio: '',
            email: '',
            asegurador: '',
            tel: '',
            notas: '',
            proxcita: '',
            foto: '',
            aviso: ''
        });
        setOpen(false);
    };

    const handleSelectPatient = (patient) => {
        setSelectedPatient(patient);
        setNewPatient(patient);
        setOpen(true);
    };

    const handleUpdatePatient = async () => {
        try {
            await axios.put(`${API_URL}/api/identificacion/${newPatient.id}`, newPatient);
            setPatients(patients.map(patient => patient.id === newPatient.id ? newPatient : patient));
        } catch (error) {
            console.error('Error al modificar el paciente:', error);
        }
        setSelectedPatient(null);
        setNewPatient({
            nombre: '',
            birthdate: '',
            sexo: '',
            edocivil: '',
            numid: '',
            domicilio: '',
            email: '',
            asegurador: '',
            tel: '',
            notas: '',
            proxcita: '',
            foto: '',
            aviso: ''
        });
        setOpen(false);
    };

    const handleDeletePatient = async () => {
        try {
            await axios.delete(`${API_URL}/api/identificacion/${deletePatientId}`);
            setPatients(patients.filter(patient => patient.id !== deletePatientId));
        } catch (error) {
            console.error('Error al eliminar el paciente:', error);
        }
        setDeletePatientId(null);
        setDeleteDialogOpen(false);
    };

    const handleClickOpen = () => {
        setSelectedPatient(null);
        setNewPatient({
            nombre: '',
            birthdate: '',
            sexo: '',
            edocivil: '',
            numid: '',
            domicilio: '',
            email: '',
            asegurador: '',
            tel: '',
            notas: '',
            proxcita: '',
            foto: '',
            aviso: ''
        });
        setAge('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteClickOpen = (id) => {
        setDeletePatientId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteClickClose = () => {
        setDeleteDialogOpen(false);
    };

    const columns = [
        { field: 'nombre', headerName: 'Nombre', width: 150 },
        { field: 'birthdate', headerName: 'Fecha de Nacimiento', width: 150 },
        { field: 'sexo', headerName: 'Sexo', width: 100 },
        { field: 'edocivil', headerName: 'Estado Civil', width: 150 },
        { field: 'numid', headerName: 'N° Identificación', width: 150 },
        { field: 'domicilio', headerName: 'Domicilio', width: 250 },
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'tel', headerName: 'Teléfono', width: 150 },
        { field: 'notas', headerName: 'Notas', width: 250 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 200,
            renderCell: (params) => (
                <strong>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<Edit />}
                        onClick={() => handleSelectPatient(params.row)}
                        sx={{ marginRight: 1 }}
                    >
                        Modificar
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        startIcon={<Delete />}
                        onClick={() => handleDeleteClickOpen(params.row.id)}
                    >
                        Eliminar
                    </Button>
                </strong>
            )
        }
    ];

    const filteredPatients = patients.filter(patient => 
        patient.nombre.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box 
            className="center-align" 
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5'
            }}
        >
            <Paper 
                elevation={3} 
                sx={{ 
                    padding: 4, 
                    borderRadius: 2, 
                    width: '100%', 
                    maxWidth: '1200px', 
                    margin: 'auto',
                    boxSizing: 'border-box'
                }}
            >
                <Typography variant="h4" gutterBottom>Gestión de Pacientes</Typography>
                <Grid container spacing={3} sx={{ marginBottom: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField 
                            label="Buscar Paciente" 
                            variant="outlined" 
                            fullWidth 
                            margin="normal" 
                            value={search} 
                            onChange={handleSearchChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<Add />}
                            onClick={handleClickOpen}
                            sx={{ height: 'fit-content' }}
                        >
                            Agregar Paciente
                        </Button>
                    </Grid>
                </Grid>
                <div style={{ height: 500, width: '100%' }}>
                    <DataGrid
                        rows={filteredPatients}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        disableSelectionOnClick
                    />
                </div>
                <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">{selectedPatient ? 'Modificar Paciente' : 'Agregar Paciente'}
                                        </DialogTitle>
                                        <DialogContent>
                                            <DialogContentText>
                                                {selectedPatient ? 'Modificar los detalles del paciente.' : 'Ingrese los detalles del nuevo paciente.'}
                                            </DialogContentText>
                                            <TextField 
                                                autoFocus
                                                margin="dense"
                                                label="Nombre"
                                                type="text"
                                                fullWidth
                                                name="nombre"
                                                value={newPatient.nombre}
                                                onChange={handleInputChange}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Fecha de Nacimiento"
                                                type="date"
                                                fullWidth
                                                name="birthdate"
                                                value={newPatient.birthdate}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Edad"
                                                type="text"
                                                fullWidth
                                                name="edad"
                                                value={age}
                                                disabled
                                            />
                                            <TextField 
                                                select
                                                margin="dense"
                                                label="Sexo"
                                                fullWidth
                                                name="sexo"
                                                value={newPatient.sexo}
                                                onChange={handleInputChange}
                                            >
                                                <MenuItem value="Masculino">Masculino</MenuItem>
                                                <MenuItem value="Femenino">Femenino</MenuItem>
                                            </TextField>
                                            <TextField 
                                                select
                                                margin="dense"
                                                label="Estado Civil"
                                                fullWidth
                                                name="edocivil"
                                                value={newPatient.edocivil}
                                                onChange={handleInputChange}
                                            >
                                                <MenuItem value="Casado">Casado</MenuItem>
                                                <MenuItem value="Divorciado">Divorciado</MenuItem>
                                                <MenuItem value="Soltero">Soltero</MenuItem>
                                                <MenuItem value="Union Libre">Unión Libre</MenuItem>
                                                <MenuItem value="Viudo">Viudo</MenuItem>
                                            </TextField>
                                            <TextField 
                                                margin="dense"
                                                label="N° Identificación"
                                                type="number"
                                                fullWidth
                                                name="numid"
                                                value={newPatient.numid}
                                                onChange={handleInputChange}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Domicilio"
                                                type="text"
                                                fullWidth
                                                name="domicilio"
                                                value={newPatient.domicilio}
                                                onChange={handleInputChange}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Email"
                                                type="email"
                                                fullWidth
                                                name="email"
                                                value={newPatient.email}
                                                onChange={(e) => {
                                                    if (validator.isEmail(e.target.value)) {
                                                        handleInputChange(e);
                                                    } else {
                                                        alert('Email no es válido');
                                                    }
                                                }}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Teléfono"
                                                type="tel"
                                                fullWidth
                                                name="tel"
                                                value={newPatient.tel}
                                                onChange={(e) => {
                                                    if (validator.isNumeric(e.target.value)) {
                                                        handleInputChange(e);
                                                    } else {
                                                        alert('Teléfono debe contener solo números');
                                                    }
                                                }}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Notas"
                                                type="text"
                                                fullWidth
                                                name="notas"
                                                value={newPatient.notas}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={2}
                                            />
                                            <TextField 
                                                margin="dense"
                                                label="Próxima Cita"
                                                type="date"
                                                fullWidth
                                                name="proxcita"
                                                value={newPatient.proxcita}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleClose} color="primary">
                                                Cancelar
                                            </Button>
                                            <Button onClick={selectedPatient ? handleUpdatePatient : handleAddPatient} color="primary">
                                                {selectedPatient ? 'Modificar' : 'Agregar'}
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog open={deleteDialogOpen} onClose={handleDeleteClickClose} aria-labelledby="delete-dialog-title">
                                        <DialogTitle id="delete-dialog-title">Confirmar Eliminación</DialogTitle>
                                        <DialogContent>
                                            <DialogContentText>
                                                ¿Estás seguro de que deseas eliminar este paciente?
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleDeleteClickClose} color="primary">
                                                Cancelar
                                            </Button>
                                            <Button onClick={handleDeletePatient} color="secondary">
                                                Eliminar
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </Paper>
                            </Box>
                        );
                    };
                    
                    export default PatientView;
                    